import React, { useEffect, useRef, useState } from 'react';
import { Edit, Key, Copy, Check, Tag, X, AlertTriangle, ShieldCheck, ArrowLeft, Camera, Mail, Phone } from 'lucide-react';
import { toast } from 'sonner';
import { usersApi } from '../../lib/api';
import { useAuthStore } from '../../lib/store';
import { canManageRole } from '../../lib/rolePermissions';
import { cn, roleLabels, qualifikationLabels } from '../../lib/utils';
import { DIENSTGRAD_CONFIG, HELM_QUALIFIKATION_LABELS, getHelmetForDienstgrad } from '../../lib/dienstgrade';
import { FahrzeugFreigabenEditor } from './FahrzeugFreigabenEditor';
import QualificationMark from './QualificationMark';
import { EmployeePhotoUpload } from './PersonalDetailsEditor';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { dienstgradOptions, qualifikationOptions, rolleOptions } from './employeeOptions';
import { tagAbbreviation } from './tagAbbreviation';
import './employee-profile.css';
const dienstgradConfig = DIENSTGRAD_CONFIG;

function ProfileSection({ title, children }) {
  return <section className="employee-section"><h3>{title}</h3><div className="employee-section-content">{children}</div></section>;
}
function Info({ label, value }) {
  return <div><dt>{label}</dt><dd>{value || <span className="text-muted-foreground font-normal">—</span>}</dd></div>;
}
function Empty({ children }) { return <p className="text-sm text-muted-foreground">{children}</p>; }

export default function UserDetailDialog({ user, open, onOpenChange, abteilungen, onUpdate, canEdit, isSelf, canUpdateAllDepartments, availableTags, availableKategorien = [], canDelete, canSetZusatzberechtigungen, canAssignBenutzergruppen, availablePermissions, currentUserRolle, helmConfig = null, fahrzeuge = [] }) {
  const [activeTab, setActiveTab] = useState('profile');
  const [discardAction, setDiscardAction] = useState(null);
  const [photoOpen, setPhotoOpen] = useState(false);
  const initialForm = useRef({});
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [newPasswordModal, setNewPasswordModal] = useState({ open: false, password: '' });
  const [passwordCopied, setPasswordCopied] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (user) {
      const nextForm = {
        vorname: user.vorname || '',
        nachname: user.nachname || '',
        loginname: user.loginname || '',
        email: user.email || '',
        telefon: user.telefon || '',
        dienstgrad: user.dienstgrad || '',
        qualifikation: user.qualifikation || '',
        helm_qualifikation: user.helm_qualifikation || 'STANDARD',
        abteilung_id: user.abteilung_id || '',
        rolle: user.rolle || 'MITARBEITER',
        ist_aktiv: user.ist_aktiv ?? true,
        ist_gast: user.ist_gast ?? false,
        tag_ids: user.tag_ids || [],
        kategorien_ids: user.kategorien_ids || [],
        zusatzberechtigungen: user.zusatzberechtigungen || [],
        gesperrte_funktionen: user.gesperrte_funktionen || [],
      };
      initialForm.current = nextForm;
      setFormData(nextForm);
      setIsEditing(false);
      setActiveTab('profile');
      setPhotoOpen(false);
      setDiscardAction(null);
    }
  }, [user, open]);

  const dirty = isEditing && JSON.stringify(formData) !== JSON.stringify(initialForm.current);
  const discard = (close) => {
    setFormData(initialForm.current);
    setIsEditing(false);
    setDiscardAction(null);
    if (close) onOpenChange(false);
  };
  const requestExit = (close) => {
    if (isSaving) return;
    if (dirty) setDiscardAction(close ? 'close' : 'cancel');
    else discard(close);
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    try {
      let updatePayload = {
        ...formData,
        email: formData.email || null,
        telefon: formData.telefon || null,
        dienstgrad: formData.dienstgrad || null,
        qualifikation: formData.qualifikation || null,
        tag_ids: formData.tag_ids || [],
        kategorien_ids: formData.kategorien_ids || [],
        zusatzberechtigungen: formData.zusatzberechtigungen || [],
      };
      if (!canAssignBenutzergruppen) {
        delete updatePayload.kategorien_ids;
      }
      if (!canEdit) {
        updatePayload = {
          vorname: formData.vorname, nachname: formData.nachname,
          email: formData.email || null, telefon: formData.telefon || null,
        };
      }
      const result = await usersApi.update(user.id, updatePayload);
      const auth = useAuthStore.getState();
      if (auth.user?.id === user.id) auth.setUser(result.data);
      toast.success('Mitarbeiter aktualisiert');
      initialForm.current = formData;
      setIsEditing(false);
      onUpdate?.();
    } catch (error) {
      console.error('Update error:', error);
      const detail = error.response?.data?.detail;
      let errorMsg = 'Fehler beim Speichern';
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        errorMsg = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (detail && typeof detail === 'object') {
        errorMsg = detail.msg || JSON.stringify(detail);
      }
      toast.error(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const toggleTag = (tagId) => {
    setFormData(prev => {
      const current = prev.tag_ids || [];
      if (current.includes(tagId)) {
        return { ...prev, tag_ids: current.filter(id => id !== tagId) };
      } else {
        return { ...prev, tag_ids: [...current, tagId] };
      }
    });
  };

  // Get tag names from IDs
  const userTags = (
    user?.effective_tag_ids?.length ? user.effective_tag_ids : (user?.tag_ids || [])
  )
    .map(id => availableTags.find(t => t.id === id))
    .filter(Boolean);

  const handleResetPassword = async () => {
    setIsResetting(true);
    try {
      const res = await usersApi.resetPassword(user.id);
      const newPwd = res.data.temp_passwort || res.data.temporaeres_passwort;
      setResetConfirmOpen(false);
      setPasswordCopied(false);
      setNewPasswordModal({ open: true, password: newPwd });
    } catch (error) {
      console.error('Reset password error:', error);
      toast.error('Passwort zurücksetzen fehlgeschlagen');
    } finally {
      setIsResetting(false);
    }
  };

  const handleCopyPassword = async () => {
    try {
      await navigator.clipboard.writeText(newPasswordModal.password);
      setPasswordCopied(true);
      toast.success('Passwort in Zwischenablage kopiert');
      setTimeout(() => setPasswordCopied(false), 2000);
    } catch (error) {
      console.error('Clipboard error:', error);
      toast.error('Kopieren fehlgeschlagen');
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await usersApi.deactivate(user.id);
      toast.success(`${user.vorname} ${user.nachname} wurde deaktiviert`);
      setDeleteConfirmOpen(false);
      onOpenChange(false);
      onUpdate?.();
    } catch (error) {
      console.error('Delete error:', error);
      const detail = error.response?.data?.detail;
      let errorMsg = 'Fehler beim Deaktivieren';
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        errorMsg = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      }
      toast.error(errorMsg);
    } finally {
      setIsDeleting(false);
    }
  };

  if (!user) return null;

  const dienstgradInfo = dienstgradConfig[user.dienstgrad?.toUpperCase()];
  const helmetSrc = getHelmetForDienstgrad(user.dienstgrad, helmConfig, user.helm_qualifikation);
  const startEditing = () => { setActiveTab('profile'); setPhotoOpen(false); setIsEditing(true); };
  const headerTags = userTags.map(tag => ({
    id: tag.id,
    name: tag.name,
    short: tagAbbreviation(tag),
    negative: tag.kategorie === 'LIZENZ_NEGATIV',
  }));

  return (
    <>
    <Dialog open={open} onOpenChange={(next) => { if (!next) requestExit(true); }}>
      <DialogContent className={cn('employee-profile', isEditing && 'employee-profile-editing')} data-testid="employee-profile">
        {!isEditing ? <div className="employee-read-scroll">
          <DialogHeader className="employee-hero">
            <div className="employee-hero-identity">
              <DialogTitle className="employee-name">{user.vorname} {user.nachname}</DialogTitle>
              <DialogDescription className="employee-subtitle">
                {[dienstgradInfo?.label, abteilungen.find(a => a.id === user.abteilung_id)?.name].filter(Boolean).join(' · ') || 'Mitarbeiter'}
              </DialogDescription>
              {dienstgradInfo?.image && <img className="employee-rank" src={dienstgradInfo.image} alt={dienstgradInfo.label} />}
            </div>
            <img className="employee-helmet" src={helmetSrc} alt="Diensthelm entsprechend Dienstgrad und Helm-Qualifikation" />
            {headerTags.length > 0 && <div className="employee-tag-strip" aria-label="Tags">{headerTags.map(tag => <span key={tag.id} className={cn(tag.negative && 'employee-tag-restricted')} title={tag.name} aria-label={tag.name}>{tag.negative && '− '}{tag.short}</span>)}</div>}
          </DialogHeader>
          <div className="employee-profile-intro">
            <div className="employee-profile-person">
            <div className="employee-photo-wrap">
              <Avatar className="employee-portrait">
                <AvatarImage src={user.avatar_url} alt={`Foto von ${user.vorname} ${user.nachname}`} className="object-contain" />
                <AvatarFallback>{user.vorname?.[0]}{user.nachname?.[0]}</AvatarFallback>
              </Avatar>
              {(canEdit || isSelf) && <button type="button" className="employee-photo-action" aria-label="Foto ändern" aria-expanded={photoOpen} onClick={() => setPhotoOpen(!photoOpen)}><Camera size={15} /></button>}
            </div>
            <div className="employee-profile-qualification"><QualificationMark qualification={user.qualifikation} large /></div>
            </div>
            {(canEdit || isSelf) && <div className="employee-quick-actions">
              <Button className="employee-edit" onClick={startEditing}><Edit className="mr-2 h-4 w-4" />{canEdit ? 'Bearbeiten' : 'Eigene Angaben bearbeiten'}</Button>
              {canEdit && <Button variant="ghost" className="employee-password" onClick={() => setResetConfirmOpen(true)} data-testid="reset-pw-btn"><Key className="mr-2 h-4 w-4" />Passwort zurücksetzen</Button>}
            </div>}
          </div>
          {photoOpen && <div className="employee-photo-upload"><EmployeePhotoUpload user={user} onSaved={onUpdate} /></div>}
          <div className="employee-profile-body">
            <section aria-labelledby="employee-contact-title">
              <h3 id="employee-contact-title">Kontakt</h3>
              <dl className="employee-contact-fields">
                <div><dt><Mail size={15} />E-Mail</dt><dd>{user.email ? <a href={`mailto:${user.email}`}>{user.email}</a> : 'Nicht hinterlegt'}</dd></div>
                <div><dt><Phone size={15} />Telefon</dt><dd>{user.telefon ? <a href={`tel:${user.telefon}`}>{user.telefon}</a> : 'Nicht hinterlegt'}</dd></div>
              </dl>
            </section>
            {canEdit && <section className="employee-admin-info" aria-labelledby="employee-admin-title">
              <h3 id="employee-admin-title">Verwaltung</h3>
              <dl className="employee-data-grid">
                <Info label="Hauptrolle" value={roleLabels[user.rolle]} />
                <Info label="Loginname" value={user.loginname} />
                <Info label="Zusätzliche Gruppen" value={(user.kategorien_ids || []).map(id => availableKategorien.find(k => k.id === id)?.name || 'Unbekannte Gruppe').join(' · ') || 'Keine'} />
                <Info label="Kontostatus" value={user.ist_aktiv === false ? 'Inaktiv' : 'Aktiv'} />
              </dl>
            </section>}
          </div>
        </div> : <>
          <DialogHeader className="employee-edit-header">
            <button type="button" className="employee-back" disabled={isSaving} onClick={() => requestExit(false)}><ArrowLeft size={16} />Zurück zum Profil</button>
            <DialogTitle>Mitarbeiter bearbeiten</DialogTitle>
            <DialogDescription>{user.vorname} {user.nachname}</DialogDescription>
          </DialogHeader>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="employee-tabs">
          <TabsList className="employee-tab-list" aria-label="Mitarbeiterbereiche">
            <TabsTrigger value="profile">Persönliche Angaben</TabsTrigger>
            {canEdit && <TabsTrigger value="qualifications">Tags &amp; Qualifikationen</TabsTrigger>}
            {canEdit && <TabsTrigger value="vehicles">Fahrzeugfunktionen</TabsTrigger>}
            {canEdit && <TabsTrigger value="access">Zugang &amp; Rechte</TabsTrigger>}
          </TabsList>
          <form id="employee-profile-form" onSubmit={handleSave} className="employee-scroll">
            <fieldset disabled={isSaving} className="min-w-0">
            <TabsContent value="profile" className="employee-panel">
              <ProfileSection title={isEditing ? "Persönliche Angaben" : "Kontakt"}>
                {isEditing ? <>
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="profile-vorname">Vorname</Label>
                  <Input id="profile-vorname" required value={formData.vorname}
                    onChange={(e) => setFormData(p => ({ ...p, vorname: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="profile-nachname">Nachname</Label>
                  <Input id="profile-nachname" required value={formData.nachname}
                    onChange={(e) => setFormData(p => ({ ...p, nachname: e.target.value }))}
                  />
                </div>
              </div>


              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="profile-email">E-Mail</Label>
                  <Input id="profile-email" type="email" value={formData.email}
                    onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="profile-telefon">Telefon</Label>
                  <Input id="profile-telefon" type="tel" value={formData.telefon}
                    onChange={(e) => setFormData(p => ({ ...p, telefon: e.target.value }))}
                  />
                </div>
              </div>


                </> : <dl className="employee-data-grid">
                  <Info label="E-Mail" value={user.email} />
                  <Info label="Telefon" value={user.telefon} />
                </dl>}
              </ProfileSection>
              {canEdit && <ProfileSection title="Dienstzuordnung">
                {isEditing && canEdit ? <>
                  <div className="grid gap-5 sm:grid-cols-2">                <div className="space-y-2">
                  <Label>Dienstgrad</Label>
                  <Select
                    value={formData.dienstgrad || 'none'}
                    onValueChange={(v) => setFormData(p => ({ ...p, dienstgrad: v === 'none' ? '' : v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Auswählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Keiner —</SelectItem>
                      {dienstgradOptions.map(group => (
                        <React.Fragment key={group.group}>
                          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted">
                            {group.group}
                          </div>
                          {group.options.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </React.Fragment>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {/* Abteilung nur für nicht-Leitungsrollen. Änderung nur ab WL-V aufwärts. */}
                {formData.rolle !== 'WACHLEITUNG' && formData.rolle !== 'WACHLEITUNG_VERTRETUNG' && (() => {
                  const abtEditable = canUpdateAllDepartments;
                  return (
                    <div className="space-y-2">
                      <Label>
                        Abteilung
                        {!abtEditable && (
                          <span className="ml-2 text-[10px] font-normal text-muted-foreground">
                            (keine wachenweite Bearbeitungsberechtigung)
                          </span>
                        )}
                      </Label>
                      <Select
                        value={formData.abteilung_id || ''}
                        onValueChange={(v) => setFormData(p => ({ ...p, abteilung_id: v }))}
                        disabled={!abtEditable}
                      >
                        <SelectTrigger data-testid="edit-abteilung-select">
                          <SelectValue placeholder="Auswählen..." />
                        </SelectTrigger>
                        <SelectContent>
                          {abteilungen.map(abt => (
                            <SelectItem key={abt.id} value={abt.id}>{abt.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })()}
</div>
              {/* Gast-Toggle: außerhalb der Stammkraft, separater Block in der Einteilung */}
              <div className="space-y-2 p-3 rounded-lg border border-border bg-muted/20">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.ist_gast || false}
                    onChange={(e) => setFormData(p => ({ ...p, ist_gast: e.target.checked }))}
                    className="mt-0.5"
                    data-testid="edit-ist-gast-toggle"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-foreground">Gast-Mitarbeiter</p>
                    <p className="text-xs text-muted-foreground">
                      Wird im Gäste-Block der Einteilung angezeigt (nicht zur regulären Stammkraft zählend).
                      Geeignet für externe Aushilfen / Wachfremde, die unregelmäßig Dienste machen.
                    </p>
                  </div>
                </label>
              </div>


                </> : <dl className="employee-data-grid">
                  <Info label="Dienstgrad" value={dienstgradInfo?.label || user.dienstgrad} />
                  <Info label="Wachabteilung" value={abteilungen.find(a => a.id === user.abteilung_id)?.name} />
                  <Info label="Hauptrolle" value={roleLabels[user.rolle]} />
                  <Info label="Rettungsdienst" value={qualifikationLabels[user.qualifikation] || user.qualifikation} />
                </dl>}
              </ProfileSection>}
            </TabsContent>
            <TabsContent value="qualifications" className="employee-panel">
              <ProfileSection title="Qualifikationen">
                {isEditing && canEdit ? <div className="grid gap-5 sm:grid-cols-2">                <div className="space-y-2">
                  <Label>Qualifikation</Label>
                  <Select
                    value={formData.qualifikation || 'none'}
                    onValueChange={(v) => setFormData(p => ({ ...p, qualifikation: v === 'none' ? '' : v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Auswählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Keine —</SelectItem>
                      {qualifikationOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              <div className="space-y-2">
                <Label htmlFor="helm-qualifikation">Helm-Qualifikation</Label>
                <Select
                  value={formData.helm_qualifikation || 'STANDARD'}
                  onValueChange={(value) => setFormData(p => ({ ...p, helm_qualifikation: value }))}
                >
                  <SelectTrigger id="helm-qualifikation"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(HELM_QUALIFIKATION_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Bestimmt die Streifen am Dashboard-Helm unabhängig vom Dienstgrad.
                  Helme für gehobenen und höheren Dienst bleiben unverändert.
                </p>
              </div>

</div> : <dl className="employee-data-grid">
                  <Info label="Rettungsdienst" value={qualifikationLabels[user.qualifikation] || user.qualifikation} />
                  <Info label="Helm-Qualifikation" value={HELM_QUALIFIKATION_LABELS[user.helm_qualifikation || 'STANDARD']} />
                </dl>}
              </ProfileSection>
              <ProfileSection title="Tags & Lizenzen">
                {(user.automatic_tag_ids || []).length > 0 && <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">Über Benutzergruppen zugewiesen – diese Tags werden über die Gruppenzuordnung geändert.</p>
                  <div className="flex flex-wrap gap-2">{user.automatic_tag_ids.map(id => <span className="employee-tag" key={id}>{availableTags.find(tag => tag.id === id)?.name || 'Unbekannter Tag'}</span>)}</div>
                </div>}
                {isEditing && canEdit ? <>              {/* Tags-Auswahl */}
              {availableTags.length > 0 && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Tag className="w-4 h-4" />
                    Persönliche Tags
                  </Label>
                  <div className="flex flex-wrap gap-2 p-3 rounded-lg border bg-muted/20">
                    {availableTags.map(tag => {
                      const isSelected = formData.tag_ids?.includes(tag.id);
                      const isNegativ = tag.kategorie === 'LIZENZ_NEGATIV';
                      const farbe = tag.farbe || '#64748b';
                      return (
                        <button type="button" aria-pressed={isSelected} key={tag.id}
                          className={cn(
                            'employee-tag-select',
                            isSelected ? 'employee-tag-selected' : '',
                            isNegativ && 'font-medium'
                          )}
                          style={{ borderLeftColor: farbe }}
                          onClick={() => toggleTag(tag.id)}
                          data-testid={`tag-toggle-${tag.id}`}
                        >
                          {tag.name}
                          {isSelected && <X className="w-3 h-3 ml-1" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

{availableTags.length === 0 && <Empty>Keine Tags konfiguriert.</Empty>}</> : userTags.length ? <div className="flex flex-wrap gap-2">{userTags.map(tag => <span key={tag.id} className="employee-tag"><span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: tag.farbe || '#64748b' }} />{tag.name}{tag.kategorie === 'LIZENZ_NEGATIV' && <span className="text-muted-foreground"> · Einschränkung</span>}</span>)}</div> : <Empty>Keine Tags oder Lizenzen hinterlegt.</Empty>}
              </ProfileSection>

            </TabsContent>
            {canEdit && <TabsContent value="vehicles" className="employee-panel">
              <ProfileSection title="Fahrzeugfunktionen sperren">
                <FahrzeugFreigabenEditor
                  fahrzeuge={fahrzeuge}
                  gesperrteFunktionen={formData.gesperrte_funktionen || []}
                  onChange={value => setFormData(previous => ({ ...previous, gesperrte_funktionen: value }))}
                  readOnly={isSaving}
                />
              </ProfileSection>
            </TabsContent>}
            {canEdit && <TabsContent value="access" className="employee-panel">
              <ProfileSection title="Benutzerkonto">
                {isEditing && canEdit ? <div className="grid gap-5 sm:grid-cols-2">              <div className="space-y-2">
                <Label htmlFor="profile-loginname">Loginname</Label>
                <Input id="profile-loginname" required value={formData.loginname}
                  onChange={(e) => setFormData(p => ({ ...p, loginname: e.target.value }))}
                  className="font-mono"
                />
              </div>

                <div className="space-y-2">
                  <Label>Hauptrolle</Label>
                  <Select
                    value={formData.rolle}
                    onValueChange={(v) => setFormData(p => ({ ...p, rolle: v }))}
                  >
                    <SelectTrigger data-testid="edit-rolle-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {rolleOptions
                        .filter(opt => canManageRole(currentUserRolle, opt.value) || opt.value === formData.rolle)
                        .map(opt => {
                          const disabled = !canManageRole(currentUserRolle, opt.value);
                          return (
                            <SelectItem
                              key={opt.value}
                              value={opt.value}
                              disabled={disabled}
                            >
                              {opt.label}
                              {disabled && (
                                <span className="ml-2 text-xs text-muted-foreground">(aktuelle Rolle)</span>
                              )}
                            </SelectItem>
                          );
                        })
                      }
                    </SelectContent>
                  </Select>
                </div>
</div> : <dl className="employee-data-grid"><Info label="Loginname" value={user.loginname} /><Info label="Hauptrolle" value={roleLabels[user.rolle]} /></dl>}
              </ProfileSection>
              {canAssignBenutzergruppen && <ProfileSection title="Benutzergruppen">
                {isEditing ? <>              {/* Benutzergruppen-Auswahl (Mehrfachauswahl) */}
              {canAssignBenutzergruppen && availableKategorien.length > 0 && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Tag className="w-4 h-4" />
                    Zusätzliche Benutzergruppen
                  </Label>
                  <div className="flex flex-wrap gap-2 p-3 rounded-lg border bg-muted/20">
                    {availableKategorien.map(k => {
                      const isSelected = formData.kategorien_ids?.includes(k.id);
                      const farbe = k.farbe || '#64748b';
                      return (
                        <button type="button" aria-pressed={isSelected} key={k.id}
                          className={cn(
                            'employee-tag-select',
                            isSelected ? 'employee-tag-selected' : ''
                          )}
                          style={{ borderLeftColor: farbe }}
                          onClick={() => setFormData(prev => {
                            const cur = prev.kategorien_ids || [];
                            return cur.includes(k.id)
                              ? { ...prev, kategorien_ids: cur.filter(id => id !== k.id) }
                              : { ...prev, kategorien_ids: [...cur, k.id] };
                          })}
                          data-testid={`kategorie-toggle-${k.id}`}
                        >
                          {k.name}
                          {isSelected && <X className="w-3 h-3 ml-1" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

{availableKategorien.length === 0 && <Empty>Keine Benutzergruppen konfiguriert.</Empty>}</> : <div className="flex flex-wrap gap-2">{(user.kategorien_ids || []).length ? (user.kategorien_ids || []).map(id => <span className="employee-tag" key={id}>{availableKategorien.find(k => k.id === id)?.name || 'Unbekannte Gruppe'}</span>) : <Empty>Keine zusätzlichen Benutzergruppen.</Empty>}</div>}
              </ProfileSection>}
              {canSetZusatzberechtigungen && <ProfileSection title="Persönliche Berechtigungen">
                {isEditing ? <>              {/* Zusatzberechtigungen — nur sichtbar für WL/WLV/WAL */}
              {canSetZusatzberechtigungen && (
                <div className="space-y-2 p-3 rounded-lg border border-border bg-muted/20">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-foreground" />
                    <Label className="text-sm font-semibold text-foreground">Zusätzliche Rechte</Label>
                  </div>
                  <p className="text-xs text-foreground/80">
                    Granulare Rechte zusätzlich zur Rolle. Z.B. einem Mitarbeiter
                    erlauben, die Einteilung der eigenen Wachabteilung zu bearbeiten.
                  </p>
                  <div className="space-y-1 mt-2">
                    {availablePermissions.map(perm => {
                      const isChecked = (formData.zusatzberechtigungen || []).includes(perm.key);
                      return (
                        <label key={perm.key} className="flex items-center gap-2 cursor-pointer hover:bg-muted rounded px-1 py-0.5">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              setFormData(p => {
                                const cur = p.zusatzberechtigungen || [];
                                return {
                                  ...p,
                                  zusatzberechtigungen: e.target.checked
                                    ? [...cur, perm.key]
                                    : cur.filter(k => k !== perm.key),
                                };
                              });
                            }}
                            data-testid={`zusatzrecht-${perm.key}`}
                          />
                          <span className="text-sm text-foreground">{perm.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

</> : (user.zusatzberechtigungen || []).length ? <ul className="space-y-2 text-sm">{user.zusatzberechtigungen.map(key => <li key={key}>{availablePermissions.find(p => p.key === key)?.label || key}</li>)}</ul> : <Empty>Keine persönlichen Ausnahmen hinterlegt.</Empty>}
              </ProfileSection>}
              {canDelete && <ProfileSection title="Kontoverwaltung"><Button type="button" variant="ghost" className="self-start text-destructive hover:text-destructive" disabled={isSaving || dirty} onClick={() => setDeleteConfirmOpen(true)}>Mitarbeiter deaktivieren</Button>{dirty && <p className="text-xs text-muted-foreground">Änderungen zuerst speichern oder verwerfen.</p>}</ProfileSection>}

            </TabsContent>}
            </fieldset>
          </form>
        </Tabs>
        <DialogFooter className="employee-footer">
          <p className="mr-auto text-xs text-muted-foreground" role="status">{dirty ? 'Ungespeicherte Änderungen' : ''}</p>
          <>
            <Button type="button" variant="outline" disabled={isSaving} onClick={() => requestExit(false)}>Abbrechen</Button>
            <Button type="submit" form="employee-profile-form" disabled={isSaving || !dirty}>{isSaving ? 'Wird gespeichert…' : 'Änderungen speichern'}</Button>
          </>
        </DialogFooter>
        </>}
      </DialogContent>
    </Dialog>
    <AlertDialog open={!!discardAction} onOpenChange={(next) => { if (!next) setDiscardAction(null); }}>
      <AlertDialogContent>
        <AlertDialogHeader><AlertDialogTitle>Änderungen verwerfen?</AlertDialogTitle><AlertDialogDescription>Deine Änderungen wurden noch nicht gespeichert. Du kannst weiter bearbeiten oder die Änderungen verwerfen.</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter><AlertDialogCancel>Weiter bearbeiten</AlertDialogCancel><AlertDialogAction onClick={() => discard(discardAction === 'close')}>Änderungen verwerfen</AlertDialogAction></AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    {/* Delete Confirmation Dialog */}
    <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-destructive" />
            Mitarbeiter deaktivieren?
          </AlertDialogTitle>
          <AlertDialogDescription>
            Sind Sie sicher, dass Sie <span className="font-semibold">{user?.vorname} {user?.nachname}</span> deaktivieren möchten?
            <br />
            <span className="text-muted-foreground text-sm mt-2 block">
              Der Mitarbeiter wird deaktiviert und erscheint nicht mehr in der Übersicht.
              Diese Aktion kann rückgängig gemacht werden.
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Abbrechen</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={isDeleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting ? 'Wird deaktiviert...' : 'Deaktivieren'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Reset Password Confirmation */}
    <AlertDialog open={resetConfirmOpen} onOpenChange={setResetConfirmOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Key className="w-5 h-5 text-amber-600" />
            Passwort zurücksetzen?
          </AlertDialogTitle>
          <AlertDialogDescription>
            Soll für <span className="font-semibold">{user?.vorname} {user?.nachname}</span> wirklich ein neues temporäres Passwort vergeben werden?
            <br />
            <span className="text-muted-foreground text-sm mt-2 block">
              Das bisherige Passwort wird sofort ungültig. Der Mitarbeiter muss das neue Passwort beim nächsten Login ändern.
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isResetting}>Abbrechen</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleResetPassword}
            disabled={isResetting}
            data-testid="reset-pw-confirm-btn"
          >
            {isResetting ? 'Wird zurückgesetzt...' : 'Passwort vergeben'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* New Password Display Modal */}
    <Dialog
      open={newPasswordModal.open}
      onOpenChange={(open) => {
        if (!open) {
          setNewPasswordModal({ open: false, password: '' });
          setPasswordCopied(false);
        }
      }}
    >
      <DialogContent data-testid="new-pw-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key className="w-5 h-5 text-emerald-600" />
            Neues temporäres Passwort
          </DialogTitle>
          <DialogDescription>
            Für: <span className="font-semibold">{user?.vorname} {user?.nachname}</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <button
            type="button"
            onClick={handleCopyPassword}
            className="group w-full p-4 rounded-lg border-2 border-dashed border-amber-300 bg-amber-50 dark:bg-amber-900/20 hover:border-amber-500 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-all cursor-pointer"
            data-testid="new-pw-copy-btn"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="text-left">
                <p className="text-xs uppercase tracking-wide text-amber-700 dark:text-amber-300 font-medium">
                  Klicken zum Kopieren
                </p>
                <p className="font-mono font-bold text-2xl text-amber-900 dark:text-amber-100 mt-1 break-all">
                  {newPasswordModal.password}
                </p>
              </div>
              <div className="shrink-0 w-10 h-10 rounded-full bg-amber-200 dark:bg-amber-800 flex items-center justify-center group-hover:scale-110 transition-transform">
                {passwordCopied ? (
                  <Check className="w-5 h-5 text-emerald-600" />
                ) : (
                  <Copy className="w-5 h-5 text-amber-700 dark:text-amber-200" />
                )}
              </div>
            </div>
          </button>
          <p className="text-xs text-muted-foreground">
            Geben Sie das Passwort dem Mitarbeiter weiter. Es muss beim nächsten Login geändert werden.
          </p>
        </div>
        <DialogFooter>
          <Button
            onClick={() => {
              setNewPasswordModal({ open: false, password: '' });
              setPasswordCopied(false);
            }}
            data-testid="new-pw-close-btn"
          >
            Schließen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
